
<template>
  <div>
    <dialog-loading v-if="saving"></dialog-loading>
    <DialogResumePayment
      ref="resume_payment"
      @submitPayment="submitBill"
      :selected_vrts="selected_vrts_data.selected_vrts"
      :payment_ammount="payment_total"
      :bonus_ammount="'0'"
      :add_bonus="false"
      wait_client_payment
    ></DialogResumePayment>

    <v-dialog v-model="dialog" scrollable max-width="1100px" persistent transition="dialog-bottom-transition">
      <v-card class="charge-dialog-card">
        <div class="charge-dialog-header charge-dialog-header--bill">
          <div class="d-flex align-center">
            <v-avatar color="white" size="40" class="mr-3 elevation-1">
              <v-icon color="indigo darken-2">mdi-barcode</v-icon>
            </v-avatar>
            <div>
              <div class="headline white--text mb-0">Novo Boleto</div>
              <div class="caption white--text" style="opacity:.85">
                Multa 2% · Juros 1% a.m. após o vencimento
              </div>
            </div>
          </div>
        </div>

        <v-card-text v-if="all_loading" class="pa-6 text-center">
          <v-progress-circular indeterminate color="indigo" class="mb-3"></v-progress-circular>
          <div class="body-2 grey--text">Carregando dados...</div>
        </v-card-text>

        <v-card-text v-if="!all_loading" class="pa-5">
          <v-form ref="new_bill_form" lazy-validation>
            <template v-if="is_from_order">
              <v-row dense class="mb-4">
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Cliente</div>
                    <div class="subtitle-1 font-weight-medium text-truncate">{{ order_data.client_name }}</div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Total em boleto</div>
                    <div class="subtitle-1 font-weight-bold primary--text">{{ formatMoney(order_config.ammount) }}</div>
                  </div>
                </v-col>
                <v-col cols="12" v-if="paymentDescription">
                  <div class="charge-summary-card">
                    <div class="caption grey--text text--darken-1">Prazo</div>
                    <div class="body-2 font-weight-medium">{{ paymentDescription }}</div>
                  </div>
                </v-col>
              </v-row>
            </template>

            <template v-if="!is_from_order">
              <v-row dense class="mb-2">
                <v-col cols="12">
                  <SelectModality :object_with_model="one_bill" custom_label="Módulo" outlined dense preselect_active/>
                </v-col>
                <v-col cols="12" sm="8">
                  <v-autocomplete
                    v-if="allClients"
                    v-model="one_bill.client_id"
                    :items="allClients"
                    label="Cliente"
                    prepend-inner-icon="mdi-account"
                    :item-text="formatClientDisplayName"
                    item-value="id"
                    :rules="[rules.required]"
                    :loading="loading"
                    outlined
                    dense
                    hide-details="auto"
                  >
                    <template v-slot:selection="{item}">
                      {{ formatClientDisplayName(item) }} <span v-if="item.attributes.fantasyName || item.attributes.fantasy_name">({{item.attributes.fantasyName || item.attributes.fantasy_name}})</span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>Nome/Fantasia: <b>{{item.attributes.fantasyName}}</b></v-list-item-title>
                        <v-list-item-subtitle>Razão Social: <b>{{ formatClientDisplayName(item) }}</b></v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-menu
                    v-model="menu_due"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="$moment(one_bill.nf_due).format('DD/MM/YYYY')"
                        label="Vencimento"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        outlined
                        dense
                        hide-details="auto"
                        :error-messages="dueErrors(one_bill.nf_due)"
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker locale="pt-br" v-model="one_bill.nf_due" :min="today_str" @input="menu_due = false"></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </template>

            <v-alert
              v-if="need_create_nf && selected_vrts_data.selected_vrts.length > 0"
              type="error"
              dense
              text
              class="mb-4"
            >
              O pedido da fatura relacionada não possui NF-e criada. É necessário criá-la no pedido para associar ao novo boleto.
              <div class="d-flex align-center mt-1">
                Acessar pedido: #{{ selected_vrts_data.selected_vrts[0].attributes.order_id || selected_vrts_data.selected_vrts[0].attributes.external_order_id }}
                <v-btn x-small icon class="ml-1" @click="goToOrder(selected_vrts_data.selected_vrts[0].attributes)">
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </div>
            </v-alert>

            <v-row dense class="mb-4">
              <v-col cols="12" sm="5">
                <v-select
                  label="Empresa"
                  v-model="one_bill.company"
                  :items="all_companies"
                  :rules="[rules.custom_msg(one_bill.company, 'Campo obrigatório. Selecione uma fatura.')]"
                  item-value="name"
                  item-text="fantasyName"
                  prepend-inner-icon="mdi-domain"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
                  :loading="loadingNF"
                >
                  <template v-slot:item="{item}">
                    {{ item.fantasyName }}
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model="one_bill.nf_number"
                  :rules="[rules.custom_msg(one_bill.nf_number, 'Campo obrigatório. Selecione uma fatura.')]"
                  prepend-inner-icon="mdi-file-document-outline"
                  label="Nº Nota Fiscal"
                  outlined
                  dense
                  hide-details="auto"
                  readonly
                  :loading="loadingNF"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3" class="d-flex align-center">
                <v-btn
                  outlined
                  color="indigo darken-2"
                  block
                  :disabled="!one_bill.company"
                  @click="dialog_nf_infos = !dialog_nf_infos"
                >
                  <v-icon left small>mdi-eye-outline</v-icon>
                  Ver NF-e
                </v-btn>
              </v-col>
              <v-col cols="12" v-if="company_without_credentials">
                <v-alert color="error" icon="mdi-alert" class="mb-0" dense text>
                  {{ company_credentials_message }}
                </v-alert>
              </v-col>
            </v-row>

            <v-dialog v-model="dialog_nf_infos" v-if="dialog_nf_infos" persistent max-width="500px" transition="dialog-transition">
              <v-card class="charge-dialog-card">
                <div class="charge-dialog-header charge-dialog-header--bill py-4">
                  <div class="title white--text">Dados NF-e</div>
                </div>
                <v-card-text class="pa-5">
                  <v-row dense class="mb-3">
                    <v-col cols="12">
                      <div class="charge-summary-card">
                        <div class="caption grey--text text--darken-1">Emitente</div>
                        <div class="body-2 font-weight-medium">{{ nfe.company_name }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="charge-summary-card">
                        <div class="caption grey--text text--darken-1">Valor total</div>
                        <div class="body-2 font-weight-medium">{{ formatMoney(nfe.value) }}</div>
                      </div>
                    </v-col>
                    <v-col cols="6">
                      <div class="charge-summary-card">
                        <div class="caption grey--text text--darken-1">NF</div>
                        <div class="body-2 font-weight-medium">{{ nfe.nf_number }}</div>
                      </div>
                    </v-col>
                    <v-col cols="12">
                      <div class="charge-summary-card">
                        <div class="caption grey--text text--darken-1">CNPJ</div>
                        <div class="body-2 font-weight-medium">{{ formattedNfeDocument }}</div>
                      </div>
                    </v-col>
                  </v-row>

                  <div class="d-flex align-center mb-2">
                    <span class="subtitle-2">Link NF-e {{ nfe.nf }}</span>
                    <v-spacer></v-spacer>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on" @click="copyNfLink(nfe.attached_file_url)" :disabled="!nfe.attached_file_url"><v-icon small>mdi-content-copy</v-icon></v-btn>
                      </template>
                      <span>Copiar</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn icon small v-bind="attrs" v-on="on" @click="openNfLink(nfe.attached_file_url)" :disabled="!nfe.attached_file_url"><v-icon small>mdi-open-in-new</v-icon></v-btn>
                      </template>
                      <span>Acessar</span>
                    </v-tooltip>
                  </div>

                  <FileAttachment
                    :update_infos="{MODEL: 'fiscal_note', id: nfe.id, API: 'FiscalNote'}"
                    :attached_file_url="nfe.attached_file_url"
                    :is_creating="!nfe.id"
                    ref="file_attachment"
                    direct_preview
                    img_direct_preview
                  ></FileAttachment>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-3">
                  <v-spacer></v-spacer>
                  <v-btn text @click="dialog_nf_infos = false">Fechar</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

            <template v-if="is_from_order">
              <div class="d-flex align-center mb-2">
                <div class="subtitle-2 font-weight-bold grey--text text--darken-2">Parcelas</div>
                <v-spacer></v-spacer>
                <v-chip x-small outlined>{{ bills_aux.length }}</v-chip>
              </div>

              <v-card
                v-for="(b_aux, idx) in bills_aux"
                :key="'l' + idx"
                outlined
                class="charge-parcel-card mb-3"
              >
                <v-card-text class="pa-4">
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="28" color="indigo lighten-5" class="mr-2">
                      <span class="indigo--text text--darken-2 font-weight-bold">{{ idx + 1 }}</span>
                    </v-avatar>
                    <span class="subtitle-2 font-weight-medium">Boleto {{ idx + 1 }}</span>
                  </div>
                  <v-row dense>
                    <v-col cols="12" sm="6">
                      <v-text-field
                        v-model="b_aux.value"
                        v-currency="currency"
                        :rules="[rules.required, rules.notZero]"
                        prepend-inner-icon="mdi-currency-brl"
                        label="Valor"
                        prefix="R$"
                        outlined
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12" sm="6">
                      <v-menu
                        v-model="b_aux.menu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        min-width="290px"
                        offset-y
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field
                            :value="$moment(b_aux.due).format('DD/MM/YYYY')"
                            label="Vencimento"
                            prepend-inner-icon="mdi-calendar"
                            readonly
                            outlined
                            dense
                            hide-details="auto"
                            :error-messages="dueErrors(b_aux.due)"
                            v-bind="attrs"
                            v-on="on"
                          ></v-text-field>
                        </template>
                        <v-date-picker locale="pt-br" v-model="b_aux.due" :min="today_str" @input="b_aux.menu = false"></v-date-picker>
                      </v-menu>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>

              <div
                class="charge-sum-box mt-2 mb-4"
                :class="sum_bills_same_as_order ? 'charge-sum-box--ok' : 'charge-sum-box--err'"
              >
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="caption font-weight-medium">Soma das parcelas</span>
                  <span class="subtitle-2 font-weight-bold">
                    {{ formatMoney(sum_bills) }}
                    <span class="caption font-weight-regular"> / {{ formatMoney(order_config.ammount) }}</span>
                  </span>
                </div>
                <v-progress-linear
                  :value="sumProgress"
                  height="6"
                  rounded
                  :color="sum_bills_same_as_order ? 'success' : 'warning'"
                ></v-progress-linear>
              </div>
            </template>

            <v-row dense class="mb-3">
              <v-col cols="12" sm="6">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="green lighten-5" class="mr-3">
                    <v-icon color="green darken-2">mdi-cash-multiple</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Valor do boleto</div>
                    <div v-if="Number(selected_vrts_data.selected_vrts_ammount) > 0" class="subtitle-1 font-weight-bold">
                      {{ formatMoney(selected_vrts_data.selected_vrts_ammount) }}
                    </div>
                    <div v-else class="body-2 font-italic error--text">Selecione a fatura abaixo</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="6">
                <div class="charge-summary-card d-flex align-center">
                  <v-avatar size="40" color="red lighten-5" class="mr-3">
                    <v-icon color="red darken-2">mdi-book-open-variant-outline</v-icon>
                  </v-avatar>
                  <div>
                    <div class="caption grey--text text--darken-1">Em aberto ({{ modality_name }})</div>
                    <div class="subtitle-1 font-weight-bold">{{ formatMoney(selected_vrts_data.total_debt) }}</div>
                  </div>
                </div>
              </v-col>
            </v-row>

            <VrtSelection :client_id="one_bill.client_id" @onChange="handleChangeVrtSelection" manual_values single_select></VrtSelection>
          </v-form>
        </v-card-text>

        <template v-if="!all_loading">
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false">Cancelar</v-btn>
            <v-btn
              color="indigo darken-2"
              depressed
              class="px-5 white--text"
              :disabled="!bill_ready"
              @click="showResume()"
            >
              <v-icon left small>mdi-check</v-icon>
              Gerar boleto
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import {CurrencyDirective} from 'vue-currency-input'
import Api from '@/api/index';
import cimentao_links from "@/mixins/cimentao_links";
import sicoob_credentials, { companyCredentialsWarningMessage } from '@/_backoffice/_financial/mixins/sicoob_credentials';
import clientDisplay from '@/mixins/clientDisplay';
import { isOverdue } from '@/utils/dueDate'


export default {
  name: 'DialogNewBill',
  directives: {
    currency: CurrencyDirective,
  },
  mixins: [cimentao_links, sicoob_credentials, clientDisplay],
  components: {
    VrtSelection: () => import('@/_backoffice/_financial/components/order_payment/VrtSelection.vue'),
    DialogResumePayment: () => import('@/_backoffice/_financial/components/order_payment/DialogResumePayment.vue'),
    // FiscalNoteForm: () => import('./FiscalNoteForm.vue'),
    FileAttachment: () => import('@/components/shared/FileAttachment.vue'),
    SelectModality: () => import('@/components/shared/SelectModality.vue')
  },
  props: {
    is_from_order: {
      type: Boolean,
      default: false
    },
    order_data: {
      type: Object,
    },
  },
  data() {
    return {
      allClients: [],
      menu_due: false,
      currency: {
        currency: null,
        locale: 'pt-BR',
        autoDecimalMode: true,
        decimalLength: 2,
        distractionFree:false,
      },
      rules: {
        required: v => !!v || 'Campo é necessário',
        notZeroPercentage: v => (!!v && v != '0.00' && parseFloat(v) < 100) || 'Formato válido: "99.99"',
        notZero: v=> v != '0.00' || 'Não pode ser zero',
        email: v => /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail deve ser válido',
        cpf: v => (v && v.length == 11) || 'CPF deve ser completo',
        custom_msg: (v, msg) => !!v || (msg) 
      },

      dialog: true,
      one_bill:{
        external_order_id: null,
        client_id: null,
        nf_number: null,
        company: null,
        sent: true,
        nf_due: this.$moment().add(7, 'day').format('YYYY-MM-DD'),
        value: null,
        value_aux: '0.00',
        place_id: null,
      },
      bills_aux: [],
      all_companies: [],
      bill_prepared: false,
      loading: false,
      loading_all_companies: false,
      saving: false,
      selected_vrts_data:{
        paying_vrts: [],
        selected_vrts: [],
        selected_vrts_ammount: '0.00',
        total_debt: '0.00',
      },
      nfe: {

      },
      
      need_create_nf: false,
      company_without_credentials: false,
      company_credentials_message: '',
      loadingNF: false,
      dialog_nf_infos: false,
      nf_request_id: 0,
    }
  },
  methods: {
    parseMoney(value) {
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0
      const raw = String(value ?? '').trim()
      if (!raw) return 0
      const hasComma = raw.includes(',')
      const hasDot = raw.includes('.')
      if (hasComma && hasDot) {
        if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
          return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
        }
        return Number(raw.replace(/,/g, '')) || 0
      }
      if (hasComma) return Number(raw.replace(/\./g, '').replace(',', '.')) || 0
      return Number(raw) || 0
    },
    formatMoney(value) {
      return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    goToOrder(item){
      if(item.external_order_id){
        this.goToRetailPage(item?.external_order_id)
      }else if(item.order_id){
        this.goToWholesaleDoneOrder(item?.order_id)
      }
    },
    copyNfLink(url) {
      navigator.clipboard.writeText(url).then(() => {
        alert("Link copiado com sucesso!");
      }).catch(err => {
        console.error("Erro ao copiar o link: ", err);
        alert("Não foi possível copiar o link.");
      })
    },
    openNfLink(url){
      window.open(url, "_blank")
    },
    getNf(vrt_transaction_id){
      const requestId = ++this.nf_request_id
      this.loadingNF = true
      this.company_without_credentials = false
      this.company_credentials_message = ''

      Promise.all([
        this.getSicoobCredentials(),
        this.getAllCompanies(),
      ]).then(() => {
        if (requestId !== this.nf_request_id) return null
        return Api.FiscalNote.index({vrt_transaction_id: vrt_transaction_id})
      }).then(r => {
        if (!r || requestId !== this.nf_request_id) return

        if (!r.data.fiscal_note) {
          this.need_create_nf = true
          this.nfe = {}
          this.company_without_credentials = false
          this.company_credentials_message = ''
          return
        }

        const fiscalNote = r.data.fiscal_note
        const company = this.findCompanyForFiscalNote(this.all_companies, fiscalNote)

        this.one_bill.nf_number = fiscalNote.nf_number
        this.nfe = fiscalNote
        this.need_create_nf = false

        if (company && this.hasSicoobCredentials(company)) {
          this.one_bill.company_id = company.id
          this.one_bill.fantasy_name = company.fantasyName
          this.one_bill.company = company.name
          this.company_without_credentials = false
          this.company_credentials_message = ''
        } else {
          this.one_bill.company_id = company?.id || null
          this.one_bill.fantasy_name = company?.fantasyName || null
          this.one_bill.company = null
          this.company_without_credentials = true
          this.company_credentials_message = companyCredentialsWarningMessage(
            company,
            fiscalNote.company_name
          )
        }
      }).catch(err => {
        if (requestId !== this.nf_request_id) return
        console.log({err})
      }).finally(() => {
        if (requestId === this.nf_request_id) {
          this.loadingNF = false
        }
      })
    },

    showResume(){
      if (this.company_without_credentials) {
        this.addNotification({ type: 'error', msg: this.company_credentials_message })
        return
      }
      if (!this.is_from_order && this.one_bill.nf_due && this.one_bill.nf_due < this.today_str) {
        this.addNotification({ type: 'error', msg: 'Vencimento não pode ser no passado' })
        return
      }
      if (this.is_from_order && this.bills_aux.some((b) => b.due && b.due < this.today_str)) {
        this.addNotification({ type: 'error', msg: 'Vencimento não pode ser no passado' })
        return
      }
      if (this.$refs.new_bill_form.validate() && this.selected_vrts_data.selected_vrts.length > 0) {
        this.$refs.resume_payment.openDialog()
      }else{
        this.addNotification({ type: 'error', msg: 'Verifique todos os campos!' })
      }
    },

    handleChangeVrtSelection( data ){
      this.selected_vrts_data = {...data}
      this.one_bill.value_aux = data.selected_vrts_ammount
      if (!this.is_from_order && data.selected_vrts?.length > 0) {
        this.one_bill.nf_due = this.defaultDueFromParcel(data.selected_vrts[0].attributes.DateExpired)
      }
    },
    defaultDueFromParcel(dateExpired) {
      if (!dateExpired) return this.$moment().add(1, 'day').format('YYYY-MM-DD')
      const parcelDue = this.$moment(String(dateExpired).substring(0, 10), 'YYYY-MM-DD').format('YYYY-MM-DD')
      if (isOverdue(dateExpired) || parcelDue < this.today_str) {
        return this.$moment().add(1, 'day').format('YYYY-MM-DD')
      }
      return parcelDue
    },
    dueErrors(due) {
      return due && due < this.today_str ? ['Vencimento não pode ser no passado'] : []
    },
    getClients(){
      if (this.one_bill.place_id){
        this.loading = true
        let filter = {modality_id: this.one_bill.place_id, short: true}
        Api.Client.index(filter).then(res => res.data).then((clients) => {
          const list = Array.isArray(clients.data) ? clients.data : []
          const ownClientIds = [278, 3648, 3644, 3646, 1675, 3295, 3868, 4285]
          this.allClients = list.filter(el => {
            const label = `${el.attributes?.name || ''} ${el.attributes?.corporateName || ''}`.toLowerCase()
            return !label.includes('cliente final') && !ownClientIds.includes(el.id) && !el.company_id
          })
        }).finally(()=>{
          this.loading = false
        })
      }
    },
    setupOneBill(){
      if(!this.is_from_order && !this.allClients.length){
        this.getClients()
      } else if (this.is_from_order){
        this.prepareOrderBill()
      }
      this.bill_prepared = true
      this.getSicoobCredentials().then(() => {
        this.getAllCompanies()
      })
    },
    getAllCompanies(){
      if (this.all_companies.length) {
        return Promise.resolve(this.all_companies)
      }
      if (this._companies_loading) {
        return this._companies_loading
      }

      this.loading_all_companies = true
      this._companies_loading = Api.Company.index().then(r => r.data).then(companies => {
        companies = companies.data ? companies.data.map(cc => cc.attributes) : companies
        this.all_companies = (companies || []).sort((a, b) => {
          if (a.fantasyName < b.fantasyName) return -1
          if (a.fantasyName > b.fantasyName) return 1
          return 0
        })
        return this.all_companies
      }).catch(err => {
        console.log({err})
        this.all_companies = []
        return this.all_companies
      }).finally(() => {
        this.loading_all_companies = false
        this._companies_loading = null
      })

      return this._companies_loading
    },
    prepareBill(){
      let {value_aux, client_name, ...b} = this.one_bill
      let b_aux = this.bills_aux.length ? this.bills_aux.map(b=> {
        let {value, due} = b
        return {value: String(this.parseMoney(value)), nf_due: due}
      }) : null
      let bill = {
        ...b,
        value: String(this.parseMoney(value_aux)),
        bills_aux: b_aux,
        payment_ammount: this.selected_vrts_data.selected_vrts_ammount,
        
        payment_attributes: {
          client_id: this.one_bill.client_id, 
          kind: 'bill', 
          total_value: this.one_bill.value_aux, 
          status_id: 6, 
          place_id: this.one_bill.place_id, 
          
          vrt_payments_attributes: this.selected_vrts_data.selected_vrts.map(el=> {
            return {
              vrt_transaction_id: el.attributes.id, 
              paying_ammount: Number(el.attributes.ammount - el.attributes.ammount_payed).toFixed(2)
            }
          }),
        },
        // fiscal_note_attributes: {...this.$refs.fiscal_note_form.fiscal_note}
      }
      return bill
    },
    submitBill(){
      if (this.company_without_credentials) {
        this.addNotification({ type: 'error', msg: this.company_credentials_message })
        return
      }
      if (this.$refs.new_bill_form.validate()){
        this.swallConfirmation({
          title: 'Deseja cadastrar boleto?',
          icon: 'question',
        }).then( confirmed =>{
          if (confirmed ) {
            let bill = this.prepareBill()
            this.saving = true
    
            Api.Bill.create(bill).then(r=>r.data).then(r=> {
              this.addNotification({type: 'success',  msg: `Boleto gerado com sucesso!`})
              this.$emit('doneNewBill', r)
            }).catch((err) => {
              this.addNotification({type: 'error',  msg: `Ocorreu um erro, o boleto não foi enviado!`})
              console.log({err});
            }).finally(()=>{
              this.saving = false 
            })
          }
        })

      }
    },
    prepareOrderBill() {
      this.bill_prepared = false
      this.one_bill.external_order_id = this.order_data.id
      this.one_bill.client_id = this.order_data.client_id

      this.values_aux = []
      this.dues_aux = []
      this.menu_due_aux = []
      for (let i = 0; i < this.order_config.times; i++) {
        const days_add = (i * this.order_config.daysBase) + this.order_config.offset
        this.bills_aux.push({value: '0.00', menu: false, due: this.$moment().add( days_add , 'day').format('YYYY-MM-DD')})
      }
      this.bill_prepared = true
    },
    customFilter(item, queryText, itemText){
      let {corporateName, fantasy_name, name} = item.attributes
      const text1 = name? name.toLowerCase() : ''
      const text2 = corporateName? corporateName.toLowerCase() : ''
      const fname = fantasy_name? fantasy_name.toLowerCase() : ''
      const query = queryText.toLowerCase()

      return text1.indexOf(query) > -1 || text2.indexOf(query) > -1 || fname.indexOf(query) > -1
    },
    handleClientSelect(data){
      if (this.one_bill.client_id){
        this.one_bill.client_id = (data.item.id == this.one_bill.client_id)? this.one_bill.client_id : data.item.id
      } else {
        this.one_bill.client_id = data.item.id
      }
    },
  },
  computed: {
    payment_total(){
      return this.selected_vrts_data.selected_vrts_ammount
    },
    can_submit(){
      return this.selected_vrts_data.selected_vrts.length == 0
    },
    selected_solution_name(){
      return this.modality_name
    },
    paymentDescription() {
      const d = this.order_config?.description
      if (!d) return ''
      if (typeof d === 'string') return d
      return d.description || d.attributes?.description || ''
    },
    sumProgress() {
      const target = Number(this.order_config?.ammount) || 0
      if (target <= 0) return 0
      return Math.min(100, (this.sum_bills / target) * 100)
    },
    today_str() {
      return this.$moment().format('YYYY-MM-DD')
    },
    bill_ready() {
      if (this.saving || this.loadingNF || this.need_create_nf || this.company_without_credentials) return false
      if (!this.one_bill.client_id || !this.one_bill.company || !this.one_bill.nf_number) return false
      if (!this.selected_vrts_data.selected_vrts.length) return false
      if (this.parseMoney(this.selected_vrts_data.selected_vrts_ammount) <= 0) return false
      if (this.is_from_order) {
        const parcels_ok = this.bills_aux.length > 0
          && this.bills_aux.every((b) => this.parseMoney(b.value) > 0 && !!b.due && b.due >= this.today_str)
        if (!parcels_ok) return false
      } else if (!this.one_bill.nf_due || this.one_bill.nf_due < this.today_str) {
        return false
      }
      return this.sum_bills_same_as_order
    },
    sum_bills(){
      let sum = 0
      if (this.is_from_order){
        sum = this.bills_aux.reduce((all, one)=> this.parseMoney(one.value) + all, 0 )
      }
      return Number(sum.toFixed(2))
    },
    sum_bills_same_as_order(){
      let same = true
      if (this.is_from_order){
        same = this.sum_bills.toFixed(2) === Number(this.order_config.ammount || 0).toFixed(2)
      }
      return same
    },
    formattedNfeDocument(){
      const doc = this.nfe?.company_document
      if (!doc) return ''
      const digits = String(doc).replace(/\D/g, '')
      if (digits.length !== 14) return doc
      return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
    },
    all_loading(){
      return this.loading || this.loading_all_companies
    },
    order_config() {
      let config = null
      if (this.is_from_order){
        let times = this.order_data.list.filter(({payment_option})=> payment_option.toLowerCase() == 'boleto').flat()[0]
        let {offset, daysBase, timesBase} = times.time_config

        let bill_values = this.order_data.pay_times.list.filter(({payment_option_id})=> payment_option_id == times.payment_option_id )

        config = {
          times: timesBase || 1, offset, daysBase, timesBase, description: times.payment_time, ...bill_values[0]
        }
      }
      return config
    },
    ...mapState({
      modality_name: state => state.App.activeModalityName,
    })
  },
  mounted () {
    this.setupOneBill()
  },
  watch: {
    'one_bill.company'(nv) {
      if (!nv || !this.all_companies.length) return
      const company = this.all_companies.find(c => c.name == nv)
      if (!company) return
      this.one_bill.company_id = company.id
      this.one_bill.fantasy_name = company.fantasyName
    },
    dialog(nv,ov) {
      if (nv == false){
        this.$emit('close')
      }
    },
    selected_vrts_data(nv){
      if(nv && nv.selected_vrts.length > 0){
        this.one_bill.company_id = null
        this.one_bill.company = null
        this.one_bill.fantasy_name = null
        this.one_bill.nf_number = null
        this.getNf(nv.selected_vrts[0].id)
      }
    },
    'one_bill.place_id'(nv, ov){
      if(nv && this.one_bill.place_id > 0 && ov != nv){
        this.getClients();
      }
      this.one_bill.client_id = null
    }
  },
}
</script>

<style scoped>
.charge-dialog-card {
  border-radius: 16px !important;
  overflow: hidden;
}
.charge-dialog-header {
  padding: 20px 24px;
}
.charge-dialog-header--bill {
  background: linear-gradient(135deg, #283593 0%, #3949ab 55%, #5c6bc0 100%);
}
.charge-summary-card {
  background: #f7f9fb;
  border: 1px solid #e8eef3;
  border-radius: 12px;
  padding: 12px 14px;
  height: 100%;
}
.charge-parcel-card {
  border-radius: 12px !important;
  border-color: #e8eef3 !important;
}
.charge-sum-box {
  border-radius: 12px;
  padding: 12px 14px;
  border: 1px solid #e8eef3;
  background: #fafbfc;
}
.charge-sum-box--ok {
  border-color: #a5d6a7;
  background: #f1f8f4;
}
.charge-sum-box--err {
  border-color: #ffcc80;
  background: #fff8f0;
}
</style>
